export const formatMediaTimestamp = (milliseconds: number): string => {
  const safe = Math.max(0, Math.round(milliseconds));
  const hours = Math.floor(safe / 3_600_000);
  const minutes = Math.floor((safe % 3_600_000) / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const fraction = safe % 1_000;
  const clock = hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
  return fraction ? `${clock}.${String(fraction).padStart(3, "0")}` : clock;
};

export const parseMediaTimestamp = (value: string): number => {
  const match = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?$/u.exec(value.trim());
  if (!match) throw new Error("Use a timestamp such as 5:30 or 1:05:30.250.");
  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if ((match[1] && minutes > 59) || seconds > 59) throw new Error("The timestamp is invalid.");
  const fraction = Number((match[4] ?? "").padEnd(3, "0"));
  const result = ((hours * 60 + minutes) * 60 + seconds) * 1_000 + fraction;
  if (result > 86_400_000) throw new Error("The timestamp must be within 24 hours.");
  return result;
};

export interface MediaTimestampParts {
  hours: number;
  minutes: number;
  seconds: number;
}

export const mediaTimestampParts = (milliseconds: number): MediaTimestampParts => {
  const totalSeconds = Math.floor(Math.max(0, milliseconds) / 1_000);
  return {
    hours: Math.floor(totalSeconds / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
};

export const mediaTimestampFromParts = (hours: number, minutes: number, seconds: number): number => {
  if (![hours, minutes, seconds].every(Number.isInteger)
    || hours < 0 || hours > 24
    || minutes < 0 || minutes > 59
    || seconds < 0 || seconds > 59
    || (hours === 24 && (minutes > 0 || seconds > 0))) {
    throw new Error("Enter a valid video position within 24 hours.");
  }
  return ((hours * 60 + minutes) * 60 + seconds) * 1_000;
};

export const mediaTimestampFromOptionalParts = (
  hoursValue: string,
  minutesValue: string,
  secondsValue: string,
): number | null => {
  const values = [hoursValue, minutesValue, secondsValue].map((value) => value.trim());
  if (values.every((value) => value === "")) return null;
  if (values.some((value) => value === "")) {
    throw new Error("Complete all End timestamp fields or leave them blank.");
  }
  return mediaTimestampFromParts(Number(values[0]), Number(values[1]), Number(values[2]));
};
